/**
 * PAW Record Detail Page
 *
 * Page view for PAW records (Performed Audit Websites).
 * Converted from the modal component for use when navigating from Audit Template detail.
 * Fetches data from Gobodo/RDS (Performed_Audit_Website__stg5_c).
 * Shows Related and Details tabs matching Salesforce layout.
 */

import React, { useState } from 'react';
import {
  ChevronLeft,
  FileText,
  Globe,
  AlertCircle,
  Edit2,
  Trash2,
  Loader2,
  Pencil,
  Save,
  X,
  Plus,
  Link as LinkIcon,
} from 'lucide-react';
import { usePAWRecord, useUpdatePAWRecord, PAWRecordDetail as PAWRecordDetailType } from '../../services/queries/cyberSweepQueries';
import { StandardButton } from '../../components/standard';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { AUDIT_TEMPLATES } from '../../data/auditTemplates';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { pawService } from '../../services/pawService';
import { PAWRecord } from '../../types';

interface PAWRecordDetailPageProps {
  pawId: string;
  onBack: () => void;
  onAuditClick?: (auditId: string) => void;
}

type TabType = 'related' | 'details';

// Field options
const FINDINGS_OPTIONS = [
  { value: 'Info not found', label: 'Info not found' },
  { value: 'Listing/s found and deletion has been requested', label: 'Listing/s found and deletion has been requested' },
  { value: 'Site not working', label: 'Site not working' },
  { value: 'Site Deleted Info', label: 'Site Deleted Info' },
  { value: 'Site Not Responding', label: 'Site Not Responding' },
  { value: 'Site Not Honoring', label: 'Site Not Honoring' },
  { value: 'Incomplete', label: 'Incomplete' },
  { value: 'Database non-searchable but deletion requested', label: 'Database non-searchable but deletion requested' },
  { value: 'Listing/s found', label: 'Listing/s found' },
];

const METHOD_OPTIONS = [
  { value: 'Site Opt-out form', label: 'Site Opt-out form' },
  { value: 'Site Contact Us page', label: 'Site Contact Us page' },
  { value: 'Email', label: 'Email' },
  { value: 'Phone call', label: 'Phone call' },
  { value: 'Fax', label: 'Fax' },
];

const PII_OPTIONS = [
  'Full Name',
  'Address',
  'Date of Birth',
  'Phone Number',
  'Email',
  'Business Address',
  'Business Name',
  'Mugshot',
  'ID number',
  'VIN',
  'Debit and Credit Card Numbers',
];

const AUDIT_PHASE_OPTIONS = [
  { value: 'Phase 1', label: 'Phase 1' },
  { value: 'Phase 2', label: 'Phase 2' },
  { value: 'Phase 3', label: 'Phase 3' },
];

type EditableField = 'auditPhase' | 'auditTemplate' | 'findings' | 'method' | 'piiFound' | 'phase2Required' | 'website' | 'status' | 'clientInfoLinks';

// Note type for PAW records with screenshot/image support
interface PAWNoteAttachment {
  id: string;
  name: string;
  type: string; // MIME type
  url: string; // Base64 data URL or actual URL
  size: number; // in bytes
}

interface PAWNote {
  id: string;
  body: string;
  createdBy: string;
  createdDate: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  attachments?: PAWNoteAttachment[];
}

/**
 * Format date string for display
 */
function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format PAW ID/name for display.
 * Handles Gobodo placeholder names (PAW-timestamp-seq-random) and generates clean display names.
 * @param nameOrId - The name or ID to format
 * @param numericId - Optional numeric Gobodo ID to use for clean display
 */
function formatPAWId(nameOrId: string, numericId?: string): string {
  if (!nameOrId && !numericId) return '—';

  // Check if name is a Gobodo placeholder pattern (PAW-timestamp-seq-random)
  const isGobodoPlaceholder = nameOrId && (
    /^PAW-\d{10,}-\d+-[a-z0-9]+$/i.test(nameOrId) ||
    (nameOrId.startsWith('PAW-') && nameOrId.length > 12 && /[a-z]/i.test(nameOrId.slice(4)))
  );

  // If it's a placeholder and we have a numeric ID, use that instead
  if (isGobodoPlaceholder && numericId) {
    // Use short numeric ID or last 6 digits of long one
    if (numericId.length <= 8) {
      return `PAW-${numericId}`;
    } else {
      return `PAW-${numericId.slice(-6)}`;
    }
  }

  // If it's a clean PAW name (not placeholder), return as-is
  if (nameOrId.toUpperCase().startsWith('PAW-') && !isGobodoPlaceholder) {
    return nameOrId;
  }

  // Try to use numeric ID if available
  if (numericId) {
    if (numericId.length <= 8) {
      return `PAW-${numericId}`;
    } else {
      return `PAW-${numericId.slice(-6)}`;
    }
  }

  // Extract numeric portion from name if present
  const numericMatch = nameOrId.match(/(\d+)/);
  if (numericMatch) {
    const num = numericMatch[1];
    if (num.length <= 8) {
      return `PAW-${num}`;
    } else {
      return `PAW-${num.slice(-6)}`;
    }
  }

  return nameOrId || '—';
}

const PAWRecordDetailPage: React.FC<PAWRecordDetailPageProps> = ({ pawId, onBack, onAuditClick }) => {
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Fetch the PAW record from Gobodo
  const { data: pawData, isLoading, error, refetch } = usePAWRecord(pawId);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (error || !pawData) {
    return (
      <div className="space-y-3">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            PAW Record Not Found
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ID "{pawId}" not found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </button>

        {/* Title + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded">
              <Globe className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Performed Audit Website
              </span>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {formatPAWId(pawData.name || pawId, pawData.id)}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600">
              <Edit2 className="h-3.5 w-3.5 inline mr-1" />
              Edit
            </button>
            <button className="px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
              <Trash2 className="h-3.5 w-3.5 inline mr-1" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('related')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'related'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Related
          </button>
          <button
            onClick={() => setActiveTab('details')}
            className={`py-1.5 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Details
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'related' ? (
        <RelatedTab pawData={pawData} />
      ) : (
        <DetailsTab
          pawData={pawData}
          pawId={pawId}
          onAuditClick={onAuditClick}
          onSave={() => refetch()}
        />
      )}
    </div>
  );
};

// Related Tab Component
interface RelatedTabProps {
  pawData: PAWRecordDetailType;
}

const RelatedTab: React.FC<RelatedTabProps> = ({ pawData }) => {
  const [notes, setNotes] = useState<PAWNote[]>([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newNoteAttachments, setNewNoteAttachments] = useState<PAWNoteAttachment[]>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const [editNoteAttachments, setEditNoteAttachments] = useState<PAWNoteAttachment[]>([]);
  const { userName } = useCurrentUser();

  // Handle file selection for screenshots/images
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, isEdit: boolean = false) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) {
        alert('Only image files are supported for screenshots');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const attachment: PAWNoteAttachment = {
          id: `attach-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          type: file.type,
          url: event.target?.result as string,
          size: file.size,
        };

        if (isEdit) {
          setEditNoteAttachments(prev => [...prev, attachment]);
        } else {
          setNewNoteAttachments(prev => [...prev, attachment]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    e.target.value = '';
  };

  // Handle paste for screenshots
  const handlePaste = (e: React.ClipboardEvent, isEdit: boolean = false) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        const reader = new FileReader();
        reader.onload = (event) => {
          const attachment: PAWNoteAttachment = {
            id: `attach-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: `screenshot-${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.png`,
            type: file.type,
            url: event.target?.result as string,
            size: file.size,
          };

          if (isEdit) {
            setEditNoteAttachments(prev => [...prev, attachment]);
          } else {
            setNewNoteAttachments(prev => [...prev, attachment]);
          }
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeAttachment = (attachmentId: string, isEdit: boolean = false) => {
    if (isEdit) {
      setEditNoteAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } else {
      setNewNoteAttachments(prev => prev.filter(a => a.id !== attachmentId));
    }
  };

  const handleAddNote = () => {
    if (!newNote.trim() && newNoteAttachments.length === 0) return;
    const note: PAWNote = {
      id: `note-${Date.now()}`,
      body: newNote.trim(),
      createdBy: userName,
      createdDate: new Date().toISOString(),
      attachments: newNoteAttachments.length > 0 ? newNoteAttachments : undefined,
    };
    setNotes(prev => [note, ...prev]);
    setNewNote('');
    setNewNoteAttachments([]);
    setIsAddingNote(false);
    console.log('[PAW Notes] Added note:', note);
  };

  const handleStartEditNote = (note: PAWNote) => {
    setEditingNoteId(note.id);
    setEditNoteValue(note.body);
    setEditNoteAttachments(note.attachments || []);
  };

  const handleSaveEditedNote = () => {
    if (!editingNoteId || (!editNoteValue.trim() && editNoteAttachments.length === 0)) return;
    setNotes(prev => prev.map(note =>
      note.id === editingNoteId
        ? {
            ...note,
            body: editNoteValue.trim(),
            lastModifiedBy: userName,
            lastModifiedDate: new Date().toISOString(),
            attachments: editNoteAttachments.length > 0 ? editNoteAttachments : undefined,
          }
        : note
    ));
    setEditingNoteId(null);
    setEditNoteValue('');
    setEditNoteAttachments([]);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteValue('');
    setEditNoteAttachments([]);
  };

  return (
    <div className="space-y-3">
      {/* Notes Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Notes</h3>
            <span className="text-xs text-gray-500">({notes.length})</span>
          </div>
          <button
            onClick={() => setIsAddingNote(true)}
            className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </button>
        </div>

        {/* Content */}
        <div>
          {/* Add Note Form */}
          {isAddingNote && (
            <div className="p-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <textarea
                placeholder="Enter your note... (Paste screenshots with Ctrl+V)"
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onPaste={(e) => handlePaste(e, false)}
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y text-sm"
              />

              {/* Attachment previews */}
              {newNoteAttachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {newNoteAttachments.map(attach => (
                    <div key={attach.id} className="relative group">
                      <img
                        src={attach.url}
                        alt={attach.name}
                        className="h-20 w-auto rounded border border-gray-300 dark:border-gray-600"
                      />
                      <button
                        onClick={() => removeAttachment(attach.id, false)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between items-center mt-2">
                <label className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Plus className="h-3.5 w-3.5" />
                  Add Screenshot
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFileSelect(e, false)}
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setIsAddingNote(false); setNewNote(''); setNewNoteAttachments([]); }}
                    className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim() && newNoteAttachments.length === 0}
                    className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {notes.map((note) => (
                <div key={note.id} className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                  {editingNoteId === note.id ? (
                    <div>
                      <textarea
                        value={editNoteValue}
                        onChange={(e) => setEditNoteValue(e.target.value)}
                        onPaste={(e) => handlePaste(e, true)}
                        rows={3}
                        placeholder="Edit note... (Paste screenshots with Ctrl+V)"
                        className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y text-sm"
                      />

                      {/* Edit attachment previews */}
                      {editNoteAttachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {editNoteAttachments.map(attach => (
                            <div key={attach.id} className="relative group">
                              <img
                                src={attach.url}
                                alt={attach.name}
                                className="h-20 w-auto rounded border border-gray-300 dark:border-gray-600"
                              />
                              <button
                                onClick={() => removeAttachment(attach.id, true)}
                                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-between items-center mt-2">
                        <label className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                          <Plus className="h-3.5 w-3.5" />
                          Add Screenshot
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFileSelect(e, true)}
                          />
                        </label>
                        <div className="flex gap-2">
                          <button
                            onClick={handleCancelEditNote}
                            className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEditedNote}
                            disabled={!editNoteValue.trim() && editNoteAttachments.length === 0}
                            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {note.body && (
                          <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{note.body}</p>
                        )}

                        {/* Display attachments */}
                        {note.attachments && note.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {note.attachments.map(attach => (
                              <a
                                key={attach.id}
                                href={attach.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img
                                  src={attach.url}
                                  alt={attach.name}
                                  className="max-h-48 w-auto rounded border border-gray-300 dark:border-gray-600 hover:border-blue-500 transition-colors"
                                />
                              </a>
                            ))}
                          </div>
                        )}

                        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Created By: </span>
                              <span className="font-medium text-blue-600 dark:text-blue-400">{note.createdBy}</span>
                              <span className="text-gray-400 dark:text-gray-500">, {formatDate(note.createdDate)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Last Modified By: </span>
                              {note.lastModifiedBy ? (
                                <>
                                  <span className="font-medium text-blue-600 dark:text-blue-400">{note.lastModifiedBy}</span>
                                  <span className="text-gray-400 dark:text-gray-500">, {formatDate(note.lastModifiedDate)}</span>
                                </>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleStartEditNote(note)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity flex-shrink-0"
                        title="Edit note"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : !isAddingNote && (
            <div className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
              No notes to display
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Details Tab Component
interface DetailsTabProps {
  pawData: PAWRecordDetailType;
  pawId: string;
  onAuditClick?: (auditId: string) => void;
  onSave?: () => void;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ pawData, pawId, onAuditClick, onSave }) => {
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState<string | boolean | string[]>('');
  const [newLink, setNewLink] = useState('');
  const [localData, setLocalData] = useState<PAWRecordDetailType>(pawData);
  const updatePAW = useUpdatePAWRecord();
  const { userName, userId } = useCurrentUser();

  // Parse piiFound - it may be stored as semicolon-separated string
  const getPiiFoundArray = (): string[] => {
    const pii = localData.piiFound;
    if (!pii) return [];
    return pii.split(';').map(s => s.trim()).filter(Boolean);
  };

  const handleSaveField = (fieldName: EditableField) => {
    let valueToSave = editValue;
    let gobodoValue = editValue;

    if (fieldName === 'piiFound' && Array.isArray(editValue)) {
      valueToSave = (editValue as string[]).join(';');
      gobodoValue = editValue;
    }

    const updates: Partial<PAWRecordDetailType> = { [fieldName]: valueToSave };

    if (USE_GOBODO_API) {
      const gobodoUpdates: Record<string, unknown> = {};
      const keyMap: Record<string, string> = {
        website: 'Website__c',
        auditPhase: 'Audit_Phase__c',
        auditTemplate: 'Audit_Template__c',
        findings: 'Findings__c',
        method: 'Method__c',
        piiFound: 'PII_Found__c',
        status: 'Status__c',
        phase2Required: 'Phase_2_Required__c',
        clientInfoLinks: 'Client_Info_Links__c',
      };
      Object.entries(updates).forEach(([k, v]) => {
        const value = k === 'piiFound' ? gobodoValue : v;
        gobodoUpdates[keyMap[k] ?? k] = value;
      });
      gobodoUpdates['LastModifiedById__c'] = userId;
      gobodoUpdates['LastModifiedBy__c'] = userName;
      gobodoUpdates['lastModifiedBy'] = userName;
      gobodoUpdates['LastModifiedDate__c'] = new Date().toISOString();
      gobodoUpdates['lastModifiedDate'] = new Date().toISOString();

      updatePAW.mutate(
        { id: pawId, updates: gobodoUpdates },
        {
          onSuccess: () => {
            onSave?.();
          },
          onError: (error) => {
            console.error('[PAWRecordDetailPage] Save FAILED for field:', fieldName, error);
          },
        }
      );
    } else {
      pawService.updatePAWRecord(pawId, updates as Partial<PAWRecord>);
      onSave?.();
    }

    setLocalData(prev => ({
      ...prev,
      ...updates,
      lastModifiedByName: userName,
      lastModifiedDate: new Date().toISOString()
    } as PAWRecordDetailType));
    setEditingField(null);
    setEditValue('');
  };

  const startEditing = (field: EditableField, currentValue: string | boolean | string[] | undefined) => {
    setEditingField(field);
    if (field === 'piiFound') {
      setEditValue(getPiiFoundArray());
    } else {
      setEditValue(currentValue ?? '');
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleAddLink = () => {
    if (newLink.trim()) {
      const currentLinks = localData.clientInfoLinks ? localData.clientInfoLinks.split('\n').filter(Boolean) : [];
      const newLinks = [...currentLinks, newLink.trim()];

      if (USE_GOBODO_API) {
        updatePAW.mutate(
          { id: pawId, updates: { Client_Info_Links__c: newLinks } },
          {
            onSuccess: () => onSave?.(),
            onError: (error) => console.error('[PAWRecordDetailPage] Failed to add link:', error),
          }
        );
      } else {
        pawService.updatePAWRecord(pawId, { clientInfoLinks: newLinks } as unknown as Partial<PAWRecord>);
        onSave?.();
      }

      setLocalData(prev => ({
        ...prev,
        clientInfoLinks: newLinks.join('\n'),
        lastModifiedByName: userName,
        lastModifiedDate: new Date().toISOString()
      }));
      setNewLink('');
    }
  };

  const handleRemoveLink = (index: number) => {
    const currentLinks = localData.clientInfoLinks ? localData.clientInfoLinks.split('\n').filter(Boolean) : [];
    const newLinks = currentLinks.filter((_, i) => i !== index);

    if (USE_GOBODO_API) {
      updatePAW.mutate(
        { id: pawId, updates: { Client_Info_Links__c: newLinks } },
        {
          onSuccess: () => onSave?.(),
          onError: (error) => console.error('[PAWRecordDetailPage] Failed to remove link:', error),
        }
      );
    } else {
      pawService.updatePAWRecord(pawId, { clientInfoLinks: newLinks } as unknown as Partial<PAWRecord>);
      onSave?.();
    }

    setLocalData(prev => ({
      ...prev,
      clientInfoLinks: newLinks.join('\n'),
      lastModifiedByName: userName,
      lastModifiedDate: new Date().toISOString()
    }));
  };

  const handlePiiToggle = (piiValue: string) => {
    const currentArray = Array.isArray(editValue) ? editValue as string[] : [];
    if (currentArray.includes(piiValue)) {
      setEditValue(currentArray.filter(v => v !== piiValue));
    } else {
      setEditValue([...currentArray, piiValue]);
    }
  };

  const renderValue = (value: string | undefined | null): string => {
    return value && value.trim() ? value : '—';
  };

  const renderBoolean = (value: boolean | undefined): string => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
  };

  const renderPiiFound = (): string => {
    const piiArray = getPiiFoundArray();
    if (piiArray.length === 0) return '—';
    return piiArray.join(', ');
  };

  const selectClass = "w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none";

  const renderLinks = (linksStr: string | undefined): React.ReactNode => {
    const links = linksStr ? linksStr.split('\n').filter(Boolean) : [];
    if (links.length === 0) return null;
    return (
      <div className="space-y-1">
        {links.map((link, idx) => (
          <div key={idx} className="flex items-center gap-2 group">
            <LinkIcon className="h-3 w-3 text-blue-600 flex-shrink-0" />
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline break-all text-sm"
            >
              {link}
            </a>
            <button
              onClick={() => handleRemoveLink(idx)}
              className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-opacity"
              title="Remove"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Single Fields Card - No section headers */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-3">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {/* Row 1: Performed Audit Website Name | Audit Phase */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Performed Audit Website Name</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">{formatPAWId(localData.name || pawId, localData.id)}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Audit Phase</label>
              <div className="mt-0.5">
                {editingField === 'auditPhase' ? (
                  <div className="flex items-start gap-2">
                    <select
                      value={editValue as string}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={selectClass}
                      autoFocus
                    >
                      <option value="">Select...</option>
                      {AUDIT_PHASE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button onClick={() => handleSaveField('auditPhase')} className="p-1 text-green-600 hover:text-green-800" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{renderValue(localData.auditPhase)}</span>
                    <button
                      onClick={() => startEditing('auditPhase', localData.auditPhase)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: CS Audit | Findings */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CS Audit</label>
              {localData.csAuditId ? (
                <button
                  onClick={() => onAuditClick?.(localData.csAuditId!)}
                  className="text-sm text-blue-600 hover:underline dark:text-blue-400 mt-0.5 block text-left"
                >
                  {localData.csAuditName || localData.csAuditId}
                </button>
              ) : (
                <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">—</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Findings</label>
              <div className="mt-0.5">
                {editingField === 'findings' ? (
                  <div className="flex items-start gap-2">
                    <select
                      value={editValue as string}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={selectClass}
                      autoFocus
                    >
                      <option value="">Select...</option>
                      {FINDINGS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button onClick={() => handleSaveField('findings')} className="p-1 text-green-600 hover:text-green-800" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{renderValue(localData.findings)}</span>
                    <button
                      onClick={() => startEditing('findings', localData.findings)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Row 3: Audit Template | Method */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Audit Template</label>
              <div className="mt-0.5">
                {editingField === 'auditTemplate' ? (
                  <div className="flex items-start gap-2">
                    <select
                      value={editValue as string}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={selectClass}
                      autoFocus
                    >
                      <option value="">Select...</option>
                      {AUDIT_TEMPLATES.map(tpl => (
                        <option key={tpl.id} value={tpl.name}>{tpl.name}</option>
                      ))}
                    </select>
                    <button onClick={() => handleSaveField('auditTemplate')} className="p-1 text-green-600 hover:text-green-800" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">{renderValue(localData.auditTemplateName)}</span>
                    <button
                      onClick={() => startEditing('auditTemplate', localData.auditTemplateName)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</label>
              <div className="mt-0.5">
                {editingField === 'method' ? (
                  <div className="flex items-start gap-2">
                    <select
                      value={editValue as string}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={selectClass}
                      autoFocus
                    >
                      <option value="">Select...</option>
                      {METHOD_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <button onClick={() => handleSaveField('method')} className="p-1 text-green-600 hover:text-green-800" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{renderValue(localData.method)}</span>
                    <button
                      onClick={() => startEditing('method', localData.method)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Row 4: Website | PII Found */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Website</label>
              <div className="mt-0.5">
                {editingField === 'website' ? (
                  <div className="flex items-start gap-2">
                    <input
                      type="text"
                      value={editValue as string}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      autoFocus
                    />
                    <button onClick={() => handleSaveField('website')} className="p-1 text-green-600 hover:text-green-800" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">{renderValue(localData.websiteDisplayName || localData.website)}</span>
                    <button
                      onClick={() => startEditing('website', localData.website)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">PII Found</label>
              <div className="mt-0.5">
                {editingField === 'piiFound' ? (
                  <div className="space-y-2">
                    <div className="max-h-48 overflow-y-auto border border-blue-500 rounded p-2 bg-white dark:bg-gray-700">
                      {PII_OPTIONS.map(pii => (
                        <label key={pii} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 px-1 rounded">
                          <input
                            type="checkbox"
                            checked={(editValue as string[]).includes(pii)}
                            onChange={() => handlePiiToggle(pii)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm">{pii}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <StandardButton
                        onClick={() => handleSaveField('piiFound')}
                        variant="primary"
                        size="sm"
                        leftIcon={<Save className="h-3 w-3" />}
                      >
                        Save
                      </StandardButton>
                      <StandardButton
                        onClick={cancelEditing}
                        variant="secondary"
                        size="sm"
                        leftIcon={<X className="h-3 w-3" />}
                      >
                        Cancel
                      </StandardButton>
                    </div>
                  </div>
                ) : (
                  <div className="group flex items-start gap-2">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{renderPiiFound()}</span>
                    <button
                      onClick={() => startEditing('piiFound', undefined)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity flex-shrink-0"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Row 5: Phase 2 Required | Link(s) for client information */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Phase 2 Required</label>
              <div className="mt-0.5">
                {editingField === 'phase2Required' ? (
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editValue as boolean}
                        onChange={(e) => setEditValue(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        autoFocus
                      />
                      <span className="text-sm">{(editValue as boolean) ? 'Yes' : 'No'}</span>
                    </label>
                    <button onClick={() => handleSaveField('phase2Required')} className="p-1 text-green-600 hover:text-green-800" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <span className="text-sm text-gray-900 dark:text-gray-100">{renderBoolean(localData.phase2Required)}</span>
                    <button
                      onClick={() => startEditing('phase2Required', localData.phase2Required)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                      title="Edit"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Link(s) for client information</label>
              <div className="mt-0.5">
                {renderLinks(localData.clientInfoLinks)}
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={newLink}
                    onChange={(e) => setNewLink(e.target.value)}
                    placeholder="Add link..."
                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddLink();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddLink}
                    disabled={!newLink.trim()}
                    className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Row 6: Created By | Last Modified By */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {localData.createdByName
                  ? `${localData.createdByName}, ${formatDate(localData.createdDate)}`
                  : localData.createdById
                  ? `${localData.createdById}, ${formatDate(localData.createdDate)}`
                  : formatDate(localData.createdDate) || '—'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Modified By</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-0.5">
                {localData.lastModifiedByName
                  ? `${localData.lastModifiedByName}, ${formatDate(localData.lastModifiedDate)}`
                  : localData.lastModifiedById
                  ? `${localData.lastModifiedById}, ${formatDate(localData.lastModifiedDate)}`
                  : formatDate(localData.lastModifiedDate) || '—'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PAWRecordDetailPage;
